import React from 'react';
import { Check } from 'lucide-react';
import { RobbieMark, Spinner } from '../ui';

/**
 * What a still-running part shows in place of its suppliers. With nothing
 * landed yet it fills the pane: Robbie, the wait line and the research steps,
 * centered, ticking off as the engine reports each stage. The first step is our
 * own supplier network: it is looked up before the job is sent, so it is always
 * done once research runs, with how many known suppliers it found. Once some
 * suppliers are in, it shrinks to one quiet line above them. Stages stay high
 * level on purpose, never what was searched, so the method stays ours.
 */
export const RESEARCH_STAGES = [
  'Understanding your component',
  'Finding suppliers',
  'Checking prices & stock',
  'Verifying suppliers',
  'Picking the best options',
] as const;

/** Step 1, before the engine's stages: what Hello Robbie already knew about the part. */
export const NETWORK_STEP = 'Checking our supplier network';

/** What the done network step says it found; null when the count was not recorded. */
export const networkStepDetail = (knownSuppliers?: number | null): string | null => {
  if (knownSuppliers == null || !Number.isFinite(knownSuppliers) || knownSuppliers < 0) return null;
  if (knownSuppliers === 0) return 'No known suppliers yet';
  return `${knownSuppliers} known supplier${knownSuppliers === 1 ? '' : 's'} to re-check`;
};

export const researchStageLabel = (stage?: number | null): string | null =>
  stage != null && stage >= 1 && stage <= RESEARCH_STAGES.length ? RESEARCH_STAGES[stage - 1] : null;

interface ResearchStagesProps {
  line: string;
  /** 1-5 while searching; absent until the engine reports one. */
  stage?: number | null;
  /** Known suppliers the network step found; null/absent when not recorded. */
  knownSuppliers?: number | null;
  /** Only a part being searched right now spins; a waiting one sits still. */
  busy: boolean;
  /** Suppliers are already showing below: one line, no stepper. */
  compact?: boolean;
}

const StageList: React.FC<{ stage?: number | null; knownSuppliers?: number | null }> = ({ stage, knownSuppliers }) => {
  // The network step is done before the engine starts, so engine stage n is step n + 1.
  const steps = [NETWORK_STEP, ...RESEARCH_STAGES];
  const current = Math.min(Math.max(stage ?? 1, 1), RESEARCH_STAGES.length) + 1;
  const networkDetail = networkStepDetail(knownSuppliers);
  return (
    <ol className="m-0 flex list-none flex-col p-0 text-left" aria-label="Research progress">
      {steps.map((label, i) => {
        const n = i + 1;
        const state = n < current ? 'done' : n === current ? 'active' : 'todo';
        const last = n === steps.length;
        const detail = n === 1 ? networkDetail : null;
        return (
          <li key={label} className="flex gap-s3" aria-current={state === 'active' ? 'step' : undefined}>
            <span className="flex flex-col items-center">
              <span
                className={
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-round text-[11px] font-bold transition-colors duration-500 ' +
                  (state === 'done'
                    ? 'bg-success-bg text-success-ink'
                    : state === 'active'
                      ? 'bg-action-bg text-action ring-2 ring-action/30'
                      : 'border border-line text-ink-muted')
                }
              >
                {state === 'done' ? <Check className="h-3.5 w-3.5" /> : state === 'active' ? <Spinner tone="robbie" size="sm" /> : n}
              </span>
              {!last && (
                <span
                  aria-hidden
                  className={'my-[3px] w-[2px] rounded-round transition-colors duration-500 ' + (detail ? 'h-8 ' : 'h-4 ') + (n < current ? 'bg-success-ink/40' : 'bg-line')}
                />
              )}
            </span>
            <span className="flex flex-col pt-[3px]">
              <span
                className={
                  'text-body-sm transition-colors duration-500 ' +
                  (state === 'active' ? 'font-semibold text-ink' : state === 'done' ? 'text-ink-secondary' : 'text-ink-muted')
                }
              >
                {label}
              </span>
              {detail && <span className="text-meta text-ink-muted">{detail}</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
};

export const ResearchStages: React.FC<ResearchStagesProps> = ({ line, stage, knownSuppliers, busy, compact = false }) => {
  if (compact) {
    const stageLabel = busy ? researchStageLabel(stage) : null;
    return (
      <p role="status" className="m-0 flex items-start gap-s3 px-[2px] text-body-md text-ink-secondary">
        <span className="mt-[5px] inline-flex w-[13px] shrink-0 justify-center">
          {busy ? <Spinner tone="robbie" size="sm" /> : <span className="h-[8px] w-[8px] rounded-round bg-line-strong" />}
        </span>
        <span>
          {line}
          {stageLabel && <span className="text-ink-muted"> Now: {stageLabel.toLowerCase()}.</span>}
        </span>
      </p>
    );
  }
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-s5 p-[24px] text-center">
      <RobbieMark
        size="lg"
        state={busy ? 'searching' : 'thinking'}
        aria-label={busy ? 'Robbie is researching suppliers' : 'Waiting to research suppliers'}
      />
      <p role="status" className="m-0 max-w-md text-body-md text-ink-secondary">
        {line}
      </p>
      {busy && <StageList stage={stage} knownSuppliers={knownSuppliers} />}
    </div>
  );
};

export default ResearchStages;
